import { detectionsApi } from '@/api/detections.api';
import { ActionButton } from '@/components/common/ActionButton';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { TableSkeleton } from '@/components/common/Skeleton';
import { CARD_PADDED, ICON_TILE, PANEL_TITLE, SECTION_LABEL } from '@/components/common/surfaces';
import { JSONViewDialog } from '@/components/dialogs/JSONViewDialog';
import { useDetections } from '@/hooks/useDetections';
import type { AllOrientationsHoleSets } from '@/types/detection.types';
import { Crosshair, Stack } from '@phosphor-icons/react';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { DetectionsTable } from './DetectionsTable';

interface DetectionsPanelProps {
  projectId: string;
  modelId: string | null;
  title: string;
  /** Called when regeneration completes successfully (so parent can e.g. refresh NC document). */
  onRegenerationComplete?: () => void;
  /** Called when regeneration starts or finishes (so parent can e.g. blur NC document panel). */
  onRegeneratingChange?: (regenerating: boolean) => void;
}

/**
 * There is one regeneration task per project now that a project has one STEP document, but the
 * `_target` suffix stays in the key: `APTGenerationPanel` writes and reads the very same key, and
 * dropping the suffix would orphan tasks that are already in flight in a user's localStorage.
 */
const getStorageKey = (projectId: string) => `regenerateTask_${projectId}_target`;

export const DetectionsPanel: FC<DetectionsPanelProps> = ({
  projectId,
  modelId,
  title,
  onRegenerationComplete,
  onRegeneratingChange,
}) => {
  const { t } = useTranslation();
  const { detections: allDetections, isLoading, error, refetch, deleteDetection } = useDetections(projectId);

  // Check if there's a saved task ID in localStorage (for initial state after F5)
  const storageKey = getStorageKey(projectId);
  const savedTaskId = localStorage.getItem(storageKey);

  /**
   * Filter detections by model_id.
   *
   * Memoised because the array identity is what `DetectionsTable`'s preview effect keys off: a
   * fresh array on every render restarts that effect, and its cleanup revokes the blob URLs of
   * the thumbnails currently on screen — which is what turned them into broken images.
   */
  const detections = useMemo(
    () => (modelId ? allDetections.filter((d) => d.model_id === modelId) : []),
    [allDetections, modelId]
  );
  const [showAllOrientationsDialog, setShowAllOrientationsDialog] = useState(false);
  const [allOrientationsData, setAllOrientationsData] = useState<AllOrientationsHoleSets | null>(null);
  const [isLoadingAllOrientations, setIsLoadingAllOrientations] = useState(false);
  // Initialize isRegenerating based on localStorage - button is disabled immediately after F5
  const [isRegenerating, setIsRegenerating] = useState(!!savedTaskId);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // Flag to prevent multiple task completions from triggering multiple refetches
  const taskCompletedRef = useRef(false);

  // Use refs to store functions to avoid dependency issues in callbacks
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    onRegeneratingChange?.(isRegenerating);
  }, [isRegenerating, onRegeneratingChange]);

  const handleDelete = async (id: string) => {
    await deleteDetection(id);
  };

  const pollTaskStatus = useCallback(
    async (taskId: string) => {
      // Prevent multiple completions
      if (taskCompletedRef.current) return;

      const key = getStorageKey(projectId);
      try {
        const status = await detectionsApi.getTaskStatus(taskId);

        if (status.status === 'completed') {
          // Mark as completed to prevent duplicate processing
          taskCompletedRef.current = true;

          // Task completed - stop polling and refetch detections
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          localStorage.removeItem(key);
          setIsRegenerating(false);
          toast.success(tRef.current('detections.regenerateCompleted'));
          refetchRef.current();
          onRegenerationComplete?.();
        } else if (status.status === 'failed') {
          // Mark as completed to prevent duplicate processing
          taskCompletedRef.current = true;

          // Task failed - stop polling
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          localStorage.removeItem(key);
          setIsRegenerating(false);
          toast.error(status.error || tRef.current('detections.regenerateFailed'));
        }
        // If pending or processing, continue polling
      } catch (_error) {
        // Error fetching status - stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        localStorage.removeItem(key);
        setIsRegenerating(false);
      }
    },
    [projectId]
  );

  // Check for ongoing regeneration task on mount (survives F5 refresh)
  // Using ref for pollTaskStatus to avoid re-running effect when callback changes
  const pollTaskStatusRef = useRef(pollTaskStatus);
  pollTaskStatusRef.current = pollTaskStatus;

  useEffect(() => {
    const key = getStorageKey(projectId);
    const taskIdFromStorage = localStorage.getItem(key);

    // Skip if already processed
    if (!taskIdFromStorage || taskCompletedRef.current) {
      return;
    }

    // Start polling immediately
    const checkTask = async () => {
      try {
        const status = await detectionsApi.getTaskStatus(taskIdFromStorage);
        if (status.status === 'completed' || status.status === 'failed') {
          // Mark as completed to prevent duplicate processing
          taskCompletedRef.current = true;

          // Task finished - clean up
          localStorage.removeItem(key);
          setIsRegenerating(false);
          if (status.status === 'completed') {
            toast.success(tRef.current('detections.regenerateCompleted'));
            refetchRef.current();
          } else {
            toast.error(status.error || tRef.current('detections.regenerateFailed'));
          }
        } else {
          // Task still running - start polling
          pollingIntervalRef.current = setInterval(() => {
            pollTaskStatusRef.current(taskIdFromStorage);
          }, 1000);
        }
      } catch (_error) {
        // Task not found or error - clean up
        localStorage.removeItem(key);
        setIsRegenerating(false);
      }
    };

    checkTask();
    // Only run on mount and when projectId changes
  }, [projectId]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const handleViewAllOrientations = async () => {
    if (detections.length === 0) return;

    // Use first detection to get all orientations (they all belong to the same model)
    const firstDetection = detections[0];

    setIsLoadingAllOrientations(true);
    try {
      const data = await detectionsApi.getAllHoles(firstDetection.id);
      setAllOrientationsData(data);
      setShowAllOrientationsDialog(true);
    } catch (_error) {
      toast.error(t('detections.failedToLoad'));
    } finally {
      setIsLoadingAllOrientations(false);
    }
  };

  const holesDetections = useMemo(() => detections.filter((d) => d.extraction_type === 'holes'), [detections]);

  return (
    <div className={CARD_PADDED}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <span className={ICON_TILE}>
            <Crosshair size={16} aria-hidden="true" />
          </span>
          <h2 className={PANEL_TITLE}>{title}</h2>
        </div>
        {detections.length > 0 && (
          <ActionButton
            variant="ghost"
            iconOnly={true}
            icon={<Stack size={16} aria-hidden="true" />}
            onClick={handleViewAllOrientations}
            loading={isLoadingAllOrientations}
            aria-label={t('detections.allOrientations')}
            title={t('detections.allOrientations')}
          />
        )}
      </div>

      <div className="mt-6 flex flex-col gap-4">
        {error && <ErrorMessage message={error} />}

        {isLoading || isRegenerating ? (
          /* Regeneration replaces the table wholesale, so it gets the same placeholder as the
             first load rather than a spinner where the rows were. */
          <TableSkeleton rows={6} columns={4} />
        ) : !modelId ? (
          <EmptyState
            icon={<Crosshair size={28} aria-hidden="true" />}
            title={t('detections.noModel')}
            message={t('detections.noModelMessage')}
          />
        ) : holesDetections.length === 0 ? (
          <EmptyState
            icon={<Crosshair size={28} aria-hidden="true" />}
            title={t('detections.noDetections')}
            message={t('detections.noDetectionsMessage')}
          />
        ) : (
          <>
            <div className="flex items-center gap-2">
              <span className={SECTION_LABEL}>{t('detections.holes')}</span>
              <span className="text-xs tabular-nums text-gray-400">{holesDetections.length}</span>
            </div>
            <DetectionsTable detections={holesDetections} onDelete={handleDelete} projectId={projectId} />
          </>
        )}
      </div>

      {/* All orientations dialog */}
      {allOrientationsData && (
        <JSONViewDialog
          visible={showAllOrientationsDialog}
          title={t('detections.allOrientations')}
          jsonData={allOrientationsData}
          onClose={() => setShowAllOrientationsDialog(false)}
          downloadFilename="detection_all_orientations.json"
        />
      )}
    </div>
  );
};
